import { COLLAPSE_ANIMATION } from '@reflex/ui/components/shared/animated-collapse-constants';

/**
 * Height-transition timing for the thinking block's content column.
 *
 * The column lives inside a `motion.div` that animates `height: 'auto'` so its
 * height changes don't snap. The jarring one is the active → settled collapse:
 * reasoning fills a ≤128px box while thinking, then settles to a single
 * truncated line — an instant ~100px drop that shoves everything below the
 * turn up in one frame. Animating it smooths that collapse and the
 * expand/collapse toggle of the settled text.
 *
 * The transition is deliberately OFF while the block is still active. While
 * active, reasoning streams in delta-by-delta and the inner box has its own
 * auto-scroll; animating the column's height on every chunk makes the text
 * lag a frame behind the stream instead of tracking it crisply — the same
 * jitter the collapse causes, just spread across the whole think. So the
 * height only animates for the discrete state changes (active → settled and
 * the expand/collapse toggle), and is instant (duration `0`) any time motion
 * is disabled: while streaming, or when the user prefers reduced motion.
 *
 * `reduceMotion` follows `useReducedMotion()` from motion/react: `null` means
 * the preference has not been resolved yet, which we treat as "animate" (the
 * common, no-preference case) rather than guessing the user wants stillness.
 */
export function thinkingHeightTransition(
  isActive: boolean,
  reduceMotion: boolean | null,
): { duration: number; ease: 'easeOut' } {
  const animate = !isActive && !reduceMotion;
  return {
    duration: animate ? COLLAPSE_ANIMATION.duration : 0,
    ease: COLLAPSE_ANIMATION.ease,
  };
}
